import React, { useEffect, useState } from "react";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import { Box } from "@mui/material";
import AppleIcon from '@mui/icons-material/Apple';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AndroidLogo from '../../../images/android.png';
import { companyShortName } from "../../../services/Functions";
import './MobileAppStats.scss';

const logos = {
  10: { src: require('../../../images/discologos/pitc11-removebg-preview.png'), name: "PITC" },
  11: { src: require('../../../images/discologos/lesco.jpg'), name: "LESCO" },
  12: { src: require('../../../images/discologos/gepco.jpg'), name: "GEPCO" },
  13: { src: require('../../../images/discologos/fesco.jpg'), name: "FESCO" },
  14: { src: require('../../../images/discologos/iesco logo.png'), name: "IESCO" },
  15: { src: require('../../../images/discologos/mepco1.jpg'), name: "MEPCO" },
  26: { src: require('../../../images/discologos/pesco1.jpg'), name: "PESCO" },
  27: { src: require('../../../images/discologos/hazeco_logo-removebg-preview.png'), name: "HAZECO" },
  37: { src: require('../../../images/discologos/hesco logo.png'), name: "HESCO" },
  38: { src: require('../../../images/discologos/sepco.jpg'), name: "SEPCO" },
  48: { src: require('../../../images/discologos/qesco.png'), name: "QESCO" },
  59: { src: require('../../../images/discologos/tesco.jpg'), name: "TESCO" },
  91: { src: require('../../../images/discologos/sundar-logo.png'), name: "SUNDAR" },
};

const StatRow = ({ icon, value, variant }) => (
  <div className="mobile-app-stats-stat">
    <span className={`mobile-app-stats-stat__icon mobile-app-stats-stat__icon--${variant}`}>
      {icon}
    </span>
    <span className="mobile-app-stats-stat__value">{value.toLocaleString()}</span>
  </div>
);

const DiscoStatRow = ({ icon, value }) => (
  <div className="mobile-app-stats-disco-stat">
    <span className="mobile-app-stats-disco-stat__icon">{icon}</span>
    <span className="mobile-app-stats-disco-stat__value">{value.toLocaleString()}</span>
  </div>
);

const MobileAppStats = () => {
  const [stats, setStats] = useState([]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");

        const raw = JSON.stringify({
          discoCode: '10',
        });

        const requestOptions = {
          method: "POST",
          headers: myHeaders,
          body: raw,
          redirect: "follow",
        };

        const response = await fetch(
          "https://api-powersmart.pitc.com.pk/appUser/getRegisteredAppUsers",
          requestOptions
        );

        const result = await response.json();

        if (result?.status === "200" && result?.data?.length > 0) {
          const enhancedData = result.data.map(item => ({
            ...item,
            logo: logos[item.discoCode]?.src || logos[10].src,
            companyName: logos[item.discoCode]?.name || companyShortName(item.discoCode.toString()),
          }));
          setStats(enhancedData);
        } else {
          setStats([]);
        }
      } catch (error) {
        setStats([]);
      }
    };

    fetchStats();
  }, []);

  const disco10 = stats.find(item => item.discoCode === "10");
  const otherDiscos = stats.filter(item => item.discoCode !== "10");

  const complaintCharts = disco10 ? {
    chart: { type: "pie", height: 260, backgroundColor: "transparent" },
    title: { text: "" },
    tooltip: { pointFormat: "<b>{point.y}</b> ({point.percentage:.1f}%)" },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          format: "{point.name}: {point.y} ({point.percentage:.1f}%)",
          style: { fontSize: "11px", fontWeight: "500" },
        },
      },
    },
    credits: { enabled: false },
    series: [
      {
        name: "Registered Users",
        colorByPoint: true,
        data: [
          { name: "Android Users", y: disco10.androidUsers || 0, color: '#27ae60' },
          { name: "iOS Users", y: disco10.iosUsers || 0, color: '#34495e' },
        ],
      },
    ],
  } : null;

  const comparisonChart = {
    chart: { type: "column", height: 300, backgroundColor: "transparent" },
    title: { text: "" },
    xAxis: {
      categories: otherDiscos.map(item => item.companyName),
      crosshair: true,
      labels: { style: { fontSize: "11px" } },
    },
    yAxis: {
      min: 0,
      title: { text: "Total Users", style: { fontSize: "12px" } },
      labels: {
        formatter: function () {
          return (this.value / 1000000).toFixed(2) + "M";
        },
        style: { fontSize: "11px" },
      },
      gridLineColor: "#eef2f6",
    },
    tooltip: {
      shared: true,
      useHTML: true,
      formatter: function () {
        return this.points
          .map(
            (point) =>
              `<b>${point.series.name}:</b> ${(point.y / 1000000).toFixed(2)}M<br/>`
          )
          .join("");
      },
    },
    plotOptions: {
      column: {
        pointPadding: 0.15,
        groupPadding: 0.1,
        borderWidth: 0,
        borderRadius: 3,
      },
    },
    credits: { enabled: false },
    legend: {
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
      itemStyle: { fontSize: "11px" },
    },
    series: [
      {
        name: "Android Users",
        data: otherDiscos.map(item => item.androidUsers || 0),
        color: "#27ae60",
      },
      {
        name: "iOS Users",
        data: otherDiscos.map(item => item.iosUsers || 0),
        color: "#34495e",
      },
      {
        name: "Total Users",
        data: otherDiscos.map(item => item.totalUsers || 0),
        color: "#3498db",
      },
    ],
  };

  return (
    <Box className="form-box">
      <div className="mobile-app-stats">
        <div className="mobile-app-stats__page-header">
          <img src={logos[10].src} alt="PITC Logo" />
          <h1>Power Smart Mobile App</h1>
        </div>

        {stats.length > 0 ? (
          <>
            <div className="mobile-app-stats__summary-row">
              {disco10 && (
                <div className="mobile-app-stats-card mobile-app-stats-card--summary">
                  <div className="mobile-app-stats-card__header">
                    <h2>Total Users</h2>
                  </div>
                  <div className="mobile-app-stats-card__body">
                    <div className="mobile-app-stats-summary-stats">
                      <StatRow
                        variant="android"
                        icon={<img src={AndroidLogo} alt="Android" />}
                        value={disco10.androidUsers || 0}
                      />
                      <StatRow
                        variant="ios"
                        icon={<AppleIcon />}
                        value={disco10.iosUsers || 0}
                      />
                      <StatRow
                        variant="total"
                        icon={<PeopleAltIcon />}
                        value={disco10.totalUsers || 0}
                      />
                    </div>
                    <div className="mobile-app-stats-chart-wrap">
                      <HighchartsReact highcharts={Highcharts} options={complaintCharts} />
                    </div>
                  </div>
                </div>
              )}

              <div className="mobile-app-stats-card mobile-app-stats-card--chart">
                <div className="mobile-app-stats-card__header">
                  <h2>DISCO Comparison</h2>
                </div>
                <div className="mobile-app-stats-card__body">
                  <HighchartsReact highcharts={Highcharts} options={comparisonChart} />
                </div>
              </div>
            </div>

            <div className="mobile-app-stats__disco-grid">
              {otherDiscos.map((item, index) => (
                <div className="mobile-app-stats-disco-card" key={index}>
                  <div className="mobile-app-stats-disco-card__header">
                    <h3>{item.companyName}</h3>
                  </div>
                  <div className="mobile-app-stats-disco-card__body">
                    <div className="mobile-app-stats-disco-card__logo-section">
                      <img src={item.logo} alt={`${item.companyName} Logo`} />
                    </div>
                    <div className="mobile-app-stats-disco-card__stats-section">
                      <DiscoStatRow
                        icon={<img src={AndroidLogo} alt="Android" />}
                        value={item.androidUsers || 0}
                      />
                      <DiscoStatRow
                        icon={<AppleIcon style={{ color: "#1e293b" }} />}
                        value={item.iosUsers || 0}
                      />
                      <div className="mobile-app-stats-disco-card__total-row">
                        <PeopleAltIcon />
                        <span>{(item.totalUsers || 0).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="mobile-app-stats__empty">No Data Found.</div>
        )}
      </div>
    </Box>
  );
};

export default MobileAppStats;
